import { faHandHoldingDollar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { FormField } from "../../components/ui/form-field";
import { InputGroup, InputGroupText } from "../../components/ui/input-group";
import { toast } from "../../components/ui/use-toast";
import { useFormatNow } from "../../hooks/date";
import {
  initValidation,
  validate,
  findErrors,
  isEmpty,
  isNotEqualToField,
  isNotExactLength,
  isNotMoney,
  isNotTicket,
} from "../../hooks/formValidation";
import { CustomerData, emptyCustomerData } from "../../model/CustomerData";
import { emptyPurchaseData, PurchaseData } from "../../model/PurchaseData";
import { CurrentSessionData } from "../../hooks/userSession";
import { authFetch } from "../../rest/authFetch";
import RestrictedAccess from "../../components/RestrictedAccess";

const Purchase = ({
  currentSession,
}: {
  currentSession: CurrentSessionData;
}) => {

  const [customer, setCustomer] = useState<CustomerData>(emptyCustomerData);
  const [purchase, setPurchase] = useState(emptyPurchaseData);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [validation, setValidation] = useState(
    initValidation("amount", "confirmation", "ticketNum")
  );

  const params = useParams();
  const navigate = useNavigate();
  const formatNow = useFormatNow();
  const customerUrl = useMemo(
    () => `${process.env.REACT_APP_API_BASE_URL}/customer/${params.id}`,
    [params.id]
  );

  useEffect(() => {
    authFetch(customerUrl)
      .then((response) => response.json())
      .then((result) => {
        setCustomer({
          ...emptyCustomerData,
          id: result.data.id,
          phone: result.data.phone,
          name: result.data.customer_name,
        });
        setPurchase((p) => ({ ...p, customerId: result.data.id }));
      });
  }, [customerUrl]);

  const save = () => {
    setDisableSubmit(true);
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify(purchase),
    };
    authFetch(`${process.env.REACT_APP_API_BASE_URL}/purchase/save`, requestOptions)
      .then((response) => response.json())
      .then((result) => {
        if (!result.success) {
          setDisableSubmit(false);
        }
        toast({
          variant: result.success ? "default" : "destructive",
          title: "Mensaje",
          description: result.message,
        });
      });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const isError = updateValidation(purchase);
    if (!isError) {
      save();
    }
  };

  const setField = (field: string, event: any) => {
    const updatedPurchase = { ...purchase, [field]: event.target.value.trim() };
    setPurchase(updatedPurchase);
    updateValidation(updatedPurchase, field);
  };

  const updateValidation = (purchase: PurchaseData, field = "") => {
    const newValidation = findErrors(
      purchase,
      validate(
        "amount",
        isEmpty("Debe capturar el monto de la compra"),
        isNotMoney("El monto debe ser un número")
      ),
      validate(
        "confirmation",
        isEmpty("Debe confirmar el monto de la compra"),
        isNotMoney("El monto debe ser un número"),
        isNotEqualToField("amount", "Los montos deben ser iguales")
      ),
      validate(
        "ticketNum",
        isEmpty("Debe capturar el número de ticket de pago"),
        isNotExactLength(
          4,
          "El número de ticket debe tener 4 letras o números"
        ),
        isNotTicket("El número de ticket debe contener solo letras o números")
      )
    );
    const isValidField =
      !!newValidation.fields[field] && !!validation.fields[field];
    setValidation(
      isValidField
        ? {
            ...validation,
            fields: {
              ...validation.fields,
              [field]: newValidation.fields[field],
            },
          }
        : newValidation
    );
    return newValidation.isError;
  };

  return (
    <RestrictedAccess currentSession={currentSession}>
      <div className="space-y-4">
        <div>
          <h5 className="text-lg font-semibold">
            <FontAwesomeIcon icon={faHandHoldingDollar} /> &nbsp; Acumular
            puntos
          </h5>
          <hr className="my-2" />
        </div>
        <form noValidate onSubmit={handleSubmit} className="space-y-4">
          <FormField>
            <Label htmlFor="amount">
              Compra de <strong>{customer.name}</strong>{" "}
              <small>({customer.phone})</small> por un monto de:
            </Label>
            <InputGroup>
              <InputGroupText className="rounded-r-none border-r-0">
                $
              </InputGroupText>
              <Input
                id="amount"
                type="text"
                placeholder="###.##"
                value={purchase.amount}
                aria-invalid={validation.fields["amount"].isError}
                className={
                  validation.fields["amount"].isError
                    ? "rounded-l-none border-destructive focus-visible:ring-destructive"
                    : "rounded-l-none"
                }
                onChange={(event) => setField("amount", event)}
              />
            </InputGroup>
            {validation.fields["amount"].isError && (
              <p className="text-sm text-destructive">
                {validation.fields["amount"].message}
              </p>
            )}
          </FormField>
          <FormField>
            <Label htmlFor="confirmation">Confirmar monto:</Label>
            <InputGroup>
              <InputGroupText className="rounded-r-none border-r-0">
                $
              </InputGroupText>
              <Input
                id="confirmation"
                type="text"
                placeholder="###.##"
                value={purchase.confirmation}
                aria-invalid={validation.fields["confirmation"].isError}
                className={
                  validation.fields["confirmation"].isError
                    ? "rounded-l-none border-destructive focus-visible:ring-destructive"
                    : "rounded-l-none"
                }
                onChange={(event) => setField("confirmation", event)}
              />
            </InputGroup>
            {validation.fields["confirmation"].isError && (
              <p className="text-sm text-destructive">
                {validation.fields["confirmation"].message}
              </p>
            )}
          </FormField>
          <FormField>
            <Label htmlFor="ticketNum">Número de ticket:</Label>
            <Input
              id="ticketNum"
              type="text"
              maxLength={4}
              placeholder="WXYZ"
              value={purchase.ticketNum}
              aria-invalid={validation.fields["ticketNum"].isError}
              className={
                validation.fields["ticketNum"].isError
                  ? "border-destructive focus-visible:ring-destructive"
                  : undefined
              }
              onChange={(event) => setField("ticketNum", event)}
            />
            {validation.fields["ticketNum"].isError && (
              <p className="text-sm text-destructive">
                {validation.fields["ticketNum"].message}
              </p>
            )}
          </FormField>
          <div>
            Registrada el <strong>{formatNow()}</strong> por{" "}
            <strong>admin</strong>.
          </div>
          <hr className="my-2" />
          <div className="flex justify-center gap-4">
            <Button type="button" variant="secondary" onClick={() => navigate("/")}>
              Regresar
            </Button>
            <Button type="submit" disabled={disableSubmit}>
              Guardar
            </Button>
          </div>
        </form>
      </div>
    </RestrictedAccess>
  );
};

export default Purchase;
